/**
 * Shared cosmetic-shop types (Wave 2 / v2.0a — avatar shop, economy-first).
 *
 * The avatar/cosmetic shop is the first *instant* in-app points sink: kids
 * spend earned points on cosmetics with NO parent-approval step (the whole
 * point — and the key difference from `reward_claims`, which is parent-gated).
 *
 * `COSMETIC_KINDS` is the fixed v1 enum used by:
 *  - apps/api → schema.cosmetic_items.kind check + service-layer Zod
 *  - apps/web → catalog grouping + the composited avatar preview (one
 *    equipped item per kind layers onto the portrait)
 *
 * Equip semantics (apps/api cosmetics.service.equipItem): at most ONE item
 * equipped per `kind` per member, so the preview composites a deterministic
 * stack — a `frame`, a `pet`, an `accessory` and a `theme` can all be
 * equipped at once, but equipping a second `accessory` unequips the first.
 *
 * Norwegian Bokmål-facing labels live in the web i18n bundle (`shop.kind.*`),
 * NOT here — these keys are the stable machine vocabulary.
 *
 * v1 ships placeholder geometric/recolour layers keyed by `asset_key`; real
 * hand-crafted SVG cosmetic layers are a later design drop that reuses the
 * same keys (roadmap: "ship first with placeholder layers so economy + flow
 * can be built and tested before final art lands").
 */
export const COSMETIC_KINDS = ['accessory', 'theme', 'pet', 'frame'] as const;
export type CosmeticKind = (typeof COSMETIC_KINDS)[number];

/**
 * The `point_transactions.reason` written by cosmetics.service.buyItem. A
 * negative-amount ledger row carries this reason; the points-service
 * negative-amount allow-list (INV-LED-3) must include it alongside
 * `reward_redeemed` and `adjustment`.
 */
export const COSMETIC_PURCHASE_REASON = 'cosmetic_purchased' as const;
